import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeModeProvider } from './contexts/ThemeModeContext';
import './i18n/config';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import PortalChoice from './components/Auth/PortalChoice';
import ClinicianDashboard from './components/Clinician/Dashboard';
import OrderList from './components/Clinician/OrderList';
import RadiologistDashboard from './components/Radiologist/Dashboard';
import ReaderPortal from './components/Radiologist/ReaderPortal';
import TechDashboard from './components/Technologist/TechDashboard';
import AdminDashboard from './components/Admin/Dashboard';
import FacilityAdminDashboard from './components/FacilityAdmin/Dashboard';
import ItAdminDashboard from './components/ItAdmin/Dashboard';
import ComplianceDashboard from './components/Compliance/Dashboard';
import ResetPassword from './components/Auth/ResetPassword';
import SecureViewer from './components/SecureViewer';
import SecureShare from './components/SecureShare';
import SecurePDFViewer from './components/SecurePDFViewer';
import AnonViewer from './components/AnonViewer';
import ConnectEmail from './components/EmailConnection/ConnectEmail';
import ReferringPhysicianPortal from './components/ReferringPhysician/Portal';
import PatientPortal from './components/PatientPortal/Portal';
import LaboratoryDashboard from './components/Laboratory/Dashboard';

// Import admin sub-components
import DashboardOverview from './components/Admin/DashboardOverview';
import FacilityManagementEnhanced from './components/Admin/FacilityManagement';
import UserManagement from './components/Admin/UserManagement';
import PendingApprovals from './components/Admin/PendingApprovals';
import SupportTickets from './components/Admin/SupportTickets';
import ProfileSettings from './components/Admin/ProfileSettings';
import HelpSupport from './components/Admin/HelpSupport';
import ProcedureManagement from './components/Admin/ProcedureManagement';
import ReferrerManagement from './components/Admin/ReferrerManagement';
import DepartmentManagement from './components/Admin/DepartmentManagement';

import './App.css';
import { LABORATORY_PORTAL_ROLES } from './utils/roleRoutes';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// All roles that land on the Clinician dashboard
const CLINICIAN_ROLES = [
  'clinician', 'super_clinician', 'sonographer',
  'nurse', 'medical_records_officer',
  // radiographer → primary dashboard is /technologist (imaging room workflow)
  // referring_physician has its own dedicated read-only portal at /referring-physician
];

// Roles that perform imaging acquisition — primary dashboard is /technologist
const IMAGING_TECH_ROLES = ['technologist', 'radiographer', 'sonographer'];

// All roles that can access the Radiologist reader portal (PACS reading only)
// sonographer and radiographer perform imaging — they do not interpret studies
const RADIOLOGIST_ROLES = ['radiologist', 'super_clinician', 'admin'];

// Inner App component that has access to LanguageContext
function AppContent() {
  const { language } = useLanguage();

  return (
    <Router key={language}>
      <div className="App">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/portal"
            element={
              <ProtectedRoute>
                <PortalChoice />
              </ProtectedRoute>
            }
          />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/email-connection" element={<ConnectEmail />} />
          <Route path="/secure-viewer/anon/:token" element={<AnonViewer />} />
          <Route path="/secure-share" element={<SecureShare />} />
          <Route path="/secure-viewer/:studyId" element={<SecureViewer />} />
          <Route path="/viewer/:studyId" element={<SecureViewer />} />
          <Route path="/secure-pdf/:reportId" element={<SecurePDFViewer />} />

          {/* Clinician dashboard — covers multiple ordering/clinical roles */}
          <Route
            path="/clinician"
            element={
              <ProtectedRoute allowedRoles={[...CLINICIAN_ROLES, ...IMAGING_TECH_ROLES, 'admin', 'radiologist']}>
                <ClinicianDashboard />
              </ProtectedRoute>
            }
          />

          {/* Radiologist / PACS reader */}
          <Route
            path="/radiologist"
            element={
              <ProtectedRoute allowedRoles={[...RADIOLOGIST_ROLES, 'clinician', 'super_clinician']}>
                <RadiologistDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/radiologist/reader-portal"
            element={
              <ProtectedRoute allowedRoles={[...RADIOLOGIST_ROLES, 'clinician', 'super_clinician']}>
                <ReaderPortal />
              </ProtectedRoute>
            }
          />

          {/* Order list (standalone) */}
          <Route
            path="/orders"
            element={
              <ProtectedRoute allowedRoles={[...CLINICIAN_ROLES, 'admin', 'radiologist']}>
                <OrderList />
              </ProtectedRoute>
            }
          />

          {/* Technologist / Radiographer exam room */}
          <Route
            path="/technologist"
            element={
              <ProtectedRoute allowedRoles={[...IMAGING_TECH_ROLES, 'admin']}>
                <TechDashboard />
              </ProtectedRoute>
            }
          />

          {/* Facility Admin Dashboard */}
          <Route
            path="/facility-admin/*"
            element={
              <ProtectedRoute role="facility_admin">
                <FacilityAdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* IT Admin Dashboard */}
          <Route
            path="/it-admin/*"
            element={
              <ProtectedRoute role="it_admin">
                <ItAdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Compliance Officer Dashboard (admin also allowed for oversight/breach register) */}
          <Route
            path="/compliance/*"
            element={
              <ProtectedRoute allowedRoles={['compliance_officer', 'compliance_internal', 'admin', 'it_admin']}>
                <ComplianceDashboard />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={['admin', 'tech_support', 'product_lead', 'customer_success']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<DashboardOverview />} />
            <Route path="facilities" element={<FacilityManagementEnhanced />} />
            <Route path="facilities/:id" element={<FacilityManagementEnhanced />} />
            <Route path="facilities/:id/edit" element={<FacilityManagementEnhanced />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="approvals" element={<PendingApprovals />} />
            <Route path="tickets" element={<SupportTickets />} />
            <Route path="settings" element={<ProfileSettings />} />
            <Route path="help" element={<HelpSupport />} />
            <Route path="procedures" element={<ProcedureManagement />} />
            <Route path="departments" element={<DepartmentManagement />} />
            <Route path="referrers" element={<ReferrerManagement />} />
          </Route>

          {/* Referring Physician — read-only results portal */}
          <Route
            path="/referring-physician"
            element={
              <ProtectedRoute allowedRoles={['referring_physician', 'admin']}>
                <ReferringPhysicianPortal />
              </ProtectedRoute>
            }
          />

          {/* Laboratory Information System (LIS) portal */}
          <Route
            path="/laboratory"
            element={
              <ProtectedRoute allowedRoles={LABORATORY_PORTAL_ROLES}>
                <LaboratoryDashboard />
              </ProtectedRoute>
            }
          />

          {/* Patient self-service results portal — public, handles its own MRN+DOB auth */}
          <Route path="/patient" element={<PatientPortal />} />

          <Route path="/" element={<Navigate to="/portal" />} />
          <Route path="*" element={<Navigate to="/portal" />} />
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  useEffect(() => {
    const baseURL = process.env.REACT_APP_API_URL || '';
    fetch(`${baseURL}/api/health`, { method: 'GET', credentials: 'include' }).catch(() => {});
  }, []);

  return (
    <AuthProvider>
      <ThemeModeProvider>
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </ThemeModeProvider>
    </AuthProvider>
  );
}

export default App;
